import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useWallet } from '../../contexts/WalletContext';
import CompactAutomatedBot from './CompactAutomatedBot';
import CompactManualTrading from './CompactManualTrading';
import WalletDebugInfo from '../debug/WalletDebugInfo';
import { CopyableMint } from '../common/CopyButton';
import '../../styles/compact-trading-dashboard.css';

const CompactTradingDashboard = ({ setCurrentView }) => {
  const { tokens } = useAuth();
  const { wallets, activeWallet } = useWallet();
  const { showSuccess, showError } = useToast();

  const [activeTab, setActiveTab] = useState('manual');
  const [loading, setLoading] = useState(false);

  // Real-time data
  const [dashboardData, setDashboardData] = useState({
    totalPnL: 0,
    activeBots: 0,
    todayVolume: 0,
    successRate: 0,
    activeSessions: []
  });

  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepump.urion.live/api';

  useEffect(() => {
    if (tokens?.access_token) {
      fetchDashboardData();
      connectWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [tokens]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchDashboardData = async () => {
    try {
      const [pnlResponse, sessionsResponse, statsResponse] = await Promise.all([
        makeAuthenticatedRequest('/trading/portfolio-summary'),
        makeAuthenticatedRequest('/trading/active-bots'),
        makeAuthenticatedRequest('/trading/performance-stats?timeframe=1')
      ]);

      setDashboardData({
        totalPnL: pnlResponse.data?.totalPnL || 0,
        activeBots: sessionsResponse.data?.sessions?.length || 0,
        todayVolume: statsResponse.data?.volume?.totalBuyVolume + statsResponse.data?.volume?.totalSellVolume || 0,
        successRate: statsResponse.data?.overview?.successRate || 0,
        activeSessions: sessionsResponse.data?.sessions || []
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const connectWebSocket = () => {
    // WebSocket connection logic (simplified for compact version)
    // Implementation would be similar to the full dashboard
  };

  const tabs = [
    { id: 'manual', label: 'Manual', icon: '⚡' },
    { id: 'bot', label: 'Auto Bot', icon: '🤖' },
    { id: 'sessions', label: 'Sessions', icon: '📊' }
  ];

  return (
    <div className="compact-trading-dashboard"
      style={{}}
    >
      {/* Debug component - remove in production */}
      {/* <WalletDebugInfo show={true} /> */}

      {/* Compact Header */}
      <div className="compact-header">
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-value" style={{ fontSize: 13 }}>{dashboardData.activeBots}</span>
            <span className="stat-label" style={{ fontSize: 13 }}>Active Bots</span>
          </div>
          <div className="stat-item">
            <span className={`stat-value ${dashboardData.totalPnL >= 0 ? 'positive' : 'negative'}`} style={{ fontSize: 13 }}>
              {dashboardData.totalPnL >= 0 ? '+' : ''}{dashboardData.totalPnL.toFixed(2)}%
            </span>
            <span className="stat-label" style={{ fontSize: 13 }}>P&L</span>
          </div>
          <div className="stat-item">
            <span className="stat-value" style={{ fontSize: 13 }}>{dashboardData.successRate.toFixed(0)}%</span>
            <span className="stat-label" style={{ fontSize: 13 }}>Success</span>
          </div>
          <div className="connection-status">
            <div className={`status-dot ${tokens?.access_token ? 'connected' : 'disconnected'}`}></div>
            <span className="status-text">{tokens?.access_token ? 'Online' : 'Not Logged In'}</span>
          </div>
        </div>
      </div>

      {/* Compact Tabs */}
      <div className="compact-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`compact-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-text">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Compact Content */}
      <div className="compact-content">
        {activeTab === 'manual' && (
          <CompactManualTrading
            activeWallet={activeWallet}
            onTradeComplete={fetchDashboardData}
          />
        )}

        {activeTab === 'bot' && (
          <CompactAutomatedBot
            wallets={wallets}
            activeWallet={activeWallet}
            onBotStart={fetchDashboardData}
            setCurrentView={setCurrentView}
          />
        )}

        {activeTab === 'sessions' && (
          <div className="compact-sessions">
            {dashboardData.activeSessions.length === 0 ? (
              <div className="no-sessions">
                <div className="empty-icon">🤖</div>
                <p>No active trading sessions</p>
                <button
                  className="start-bot-btn"
                  onClick={() => setActiveTab('bot')}
                >
                  Start Your First Bot
                </button>
              </div>
            ) : (
              <div className="sessions-list">
                {dashboardData.activeSessions.map(session => (
                  <div key={session.sessionId} className="compact-session-card">
                    <div className="session-header">
                      <div className="token-info">
                        <span className="token-symbol">{session.tokenSymbol || 'Unknown'}</span>
                        <div className="token-address">
                          <CopyableMint
                            mintAddress={session.tokenMint}
                            truncate={true}
                            truncateLength={8}
                          />
                        </div>
                      </div>
                      <div className={`session-status ${session.status}`}>
                        {session.status === 'active' ? '🟢' : '🟡'}
                      </div>
                    </div>

                    <div className="session-metrics">
                      <div className="metric">
                        <span className="metric-label">P&L</span>
                        <span className={`metric-value ${session.profitLoss >= 0 ? 'positive' : 'negative'}`}>
                          {session.profitLoss >= 0 ? '+' : ''}{session.profitLoss?.toFixed(2) || '0.00'}%
                        </span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Price</span>
                        <span className="metric-value">
                          ${session.currentPrice?.toFixed(6) || '0.000000'}
                        </span>
                      </div>
                    </div>

                    <div className="session-actions">
                      <button
                        className="stop-btn"
                        onClick={() => stopBot(session.sessionId)}
                        disabled={loading}
                      >
                        Stop
                      </button>
                      <button className="details-btn">
                        Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  async function stopBot(sessionId) {
    try {
      setLoading(true);
      await makeAuthenticatedRequest(`/trading/stop-bot/${sessionId}`, {
        method: 'POST'
      });
      await fetchDashboardData();
      showSuccess(`Bot ${sessionId} stopped successfully`);
    } catch (error) {
      showError(`Failed to stop bot: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }
};

export default CompactTradingDashboard;